import { Org } from "./org";
import { User } from "./user";

// 通用字段
interface EquipmentCommonFields {
  state: number; // 状态
  createdAt: number; // 创建时间
  createdBy: string; // 创建人 Id
  updatedAt: number; // 更新时间
  updatedBy: string; // 更新人 Id
}

export interface EquipmentCategoryParams {
  code: string; // 类目编号
  name: string; // 类目名称
  desc?: string | null; // 描述
  sortOrder?: number | null; // 排序
  subs?: EquipmentCategoryParams[] | null; // 子类目
}

export interface EquipmentCategory extends EquipmentCommonFields {
  id: string;
  code: string; // 类目编号
  name: string; // 类目名称
  desc: string | null; // 描述
  pId: string | null; // 父类目 Id
  rootId: string | null; // 根类目 Id
  path: string | null; // 层级路径。
  level: number; //层级
  isLeaf: number; // 是否叶子
  sortOrder: number; // 排序
  useState: number; // 启用状态
  subs: EquipmentCategory[] | null; // 子类目
}

export interface EquipmentRecordParams {
  recordType: EquipmentRecordTypeEnum; // 记录类型
  occurredAt: number; // 发生时间
  cost?: number | null; // 费用
  costCurrency?: number | null; // 币种
  odometer?: number | null; // 当时使用量读数
  vendor?: string | null; // 服务商
  comment?: string | null; // 备注
  docId?: string | null; // 附件
}

/** 设备记录类型。 */
export enum EquipmentRecordTypeEnum {
  MAINTAIN = 1, // 保养
  REPAIR = 2, // 维修
  FUEL = 3, // 加油
  CHARGE = 4, // 充电
  INSPECT = 5, // 巡检
  BORROW = 6, // 领用
  RETURN = 7, // 归还
  SCRAP = 8, // 报废
}

/** 设备业务状态。 */
export enum EquipmentStatusEnum {
  IN_STOCK = 0, // 在库
  IN_USE = 1, // 在用
  REPAIRING = 2, // 维修中
  SCRAPPED = 3, // 已报废
}

/** 设备操作和维护记录。 */
export interface EquipmentRecord extends EquipmentCommonFields {
  id: string;
  equipmentId: string; // 设备Id
  recordType: EquipmentRecordTypeEnum; // 记录类型
  occurredAt: number; // 发生时间
  operatorUserId: string | null; // 操作人Id
  operatorUser: User | null; // 操作人
  cost: number | null; // 费用
  costCurrency: number | null; // 币种
  odometer: number | null; // 使用量读数
  vendor: string | null; // 服务商
  comment: string | null; // 备注
  docId: string | null; // 附件
  borrowerUserId: string | null; // 领用人Id。
  borrowerUser: User | null; // 领用人。
  expectedReturnAt: number | null; // 预计归还时间。
  returnAt: number | null; // 实际归还时间。
  relatedRecordId: string | null; // 关联记录Id。
}

export interface EquipmentSaveParams {
  id?: string | null; // 设备Id 新增时可不传或传 null
  name: string; // 设备名称
  categoryId: string; // 类目Id
  brand?: string | null; // 品牌
  model?: string | null; // 型号
  serialNo?: string | null; // 序列号
  keeperOrgId: string; // 保管部门
  orgId?: string | null; // 购置或所属部门
  location?: string | null; // 位置。
  placeLocation?: string | null; // 放置地点。
  maintainDays?: number | null; // 保养周期(天)
  maintainThreshold?: number | null; // 保养周期(使用量阈值)
  thresholdUnit?: number | null; // 使用量单位
  purchaseAt?: number | null; // 购置时间
  purchasePrice?: number | null; // 购置价格
  purchaseCurrency?: number | null; // 币种
  supplier?: string | null; // 供应商
}

export interface EquipmentListParams {
  categoryId: string; // 类目(含子类目)
  keyword?: string | null; // 关键字(编号/名称等模糊)
  status?: number | null; // 设备状态
  keeperOrgId?: string | null; // 不传则按当前用户 orgId树形向下
}

/** 设备档案信息。 */
export interface Equipment extends EquipmentCommonFields {
  id: string; // 主键
  number: string; // 资产编号（唯一）
  qrCode: string | null; // 二维码
  name: string; // 设备名称
  categoryId: string; // 类目 Id
  category: EquipmentCategory | null; // 类目
  brand: string | null; // 品牌
  model: string | null; // 型号
  serialNo: string | null; // 序列号
  status: EquipmentStatusEnum; // 设备业务状态
  orgId: string | null; // 购置/所属部门 Id
  org: Org | null; // 购置/所属部门
  keeperOrgId: string; // 保管部门 Id
  keeperOrg: Org | null; // 保管部门
  currentUserId: string | null; // 当前持有人 Id（领用中）
  currentUser: User | null; // 当前持有人（领用中）
  location: string | null; // 位置
  placeLocation: string | null; // 放置地点
  maintainDays: number | null; // 双保养周期配置（天数）
  maintainThreshold: number | null; // 双保养周期配置（使用量）
  thresholdUnit: number | null; // 双保养周期配置（使用量单位）
  lastMaintainAt: number | null; // 上次保养时间
  lastMaintainOdometer: number | null; // 上次保养读数
  currentOdometer: number | null; // 当前读数
  purchaseAt: number | null; // 购置时间
  purchasePrice: number | null; // 购置价格
  purchaseCurrency: number | null; // 购置币种
  supplier: string | null; // 供应商
  logbookId: string | null; // 关联文档日志簿 Id
  dirId: string | null; // 关联文档目录 Id
  sequence: number; // 序号
  creator: User | null; // 创建人
  updater: User | null; // 更新人
}

export interface BorrowParams {
  expectedReturnAt?: number | null; // 预计归还时间
  comment?: string | null; // 备注
}

export interface ReturnParams {
  comment?: string | null; // 备注
}

export interface ScrapParams {
  comment?: string | null; // 报废原因备注
}

export interface MaintainUpcoming {
  equipment: Equipment; // 设备
  dueByDays: boolean; // 天数维度是否到期
  dueByThreshold: boolean; // 使用量维度是否到期
  remainDays: number | null; // 剩余天数（负数表示已超期）
  remainThreshold: number | null; // 剩余使用量（负数表示已超期）
}

/** 后端返回的设备枚举项。 */
export interface EquipmentEnumItem {
  v: number; // 枚举值。
  desc: string; // 枚举显示文案。
}

/** 设备二维码信息。 */
export interface EquipmentQrCode {
  id: string; // 设备 ID。
  number: string; // 设备或资产编号。
  qrCode: string | null; // 二维码内容，为 null 时使用设备编号生成。
}

/** 保养扫描结果。 */
export interface MaintainScan {
  dueCount: number; // 本次扫描命中的到期设备数量。
}
